import type ApolloClient from 'apollo-client';

import type {MarketId, ProductId} from 'nutshell-core/types';

import type {ProductPickerProduct} from './product-picker-product';

const PRODUCT_ID_BATCH_SIZE = 50;

type ProductBatchLoader = (productIds: ProductId[]) => Promise<ProductPickerProduct[]>;

type ProductHydrationRequest = {
    productIds: ProductId[];
    resolve: (products: ProductPickerProduct[]) => void;
    reject: (error: unknown) => void;
};

type ProductHydrationBatch = {
    loadProducts: ProductBatchLoader;
    requests: ProductHydrationRequest[];
};

const productHydrationBatches = new WeakMap<
    ApolloClient<object>,
    Map<string, ProductHydrationBatch>
>();

function getClientBatches(client: ApolloClient<object>): Map<string, ProductHydrationBatch> {
    let batches = productHydrationBatches.get(client);
    if (!batches) {
        batches = new Map();
        productHydrationBatches.set(client, batches);
    }

    return batches;
}

async function flushProductHydrationBatch(
    client: ApolloClient<object>,
    batchKey: string
): Promise<void> {
    const batches = getClientBatches(client);
    const batch = batches.get(batchKey);
    if (!batch) return;

    // New callers that arrive while these requests are running belong to the next batch.
    batches.delete(batchKey);
    const productIds = Array.from(new Set(batch.requests.flatMap((request) => request.productIds)));

    try {
        const products: ProductPickerProduct[] = [];
        for (let start = 0; start < productIds.length; start += PRODUCT_ID_BATCH_SIZE) {
            products.push(
                ...(await batch.loadProducts(
                    productIds.slice(start, start + PRODUCT_ID_BATCH_SIZE)
                ))
            );
        }

        const productsById = new Map(products.map((product) => [product.id, product]));
        batch.requests.forEach((request) => {
            request.resolve(
                request.productIds.flatMap((productId) => {
                    const product = productsById.get(productId);
                    return product ? [product] : [];
                })
            );
        });
    } catch (error: unknown) {
        batch.requests.forEach((request) => request.reject(error));
    }
}

export function enqueueProductHydration(
    client: ApolloClient<object>,
    productIds: ProductId[],
    marketId: MarketId | undefined,
    loadProducts: ProductBatchLoader
): Promise<ProductPickerProduct[]> {
    const uniqueProductIds = Array.from(new Set(productIds));
    if (uniqueProductIds.length === 0) return Promise.resolve([]);

    const batches = getClientBatches(client);
    const batchKey = marketId ?? 'no-market';
    let batch = batches.get(batchKey);
    if (!batch) {
        batch = {loadProducts, requests: []};
        batches.set(batchKey, batch);
        Promise.resolve().then(() => flushProductHydrationBatch(client, batchKey));
    }

    return new Promise<ProductPickerProduct[]>((resolve, reject) => {
        batch.requests.push({productIds: uniqueProductIds, resolve, reject});
    });
}
